/**
 * Navbar — Fixed top navigation for the public site.
 *
 * Behaviour:
 *  - Transparent over the hero; turns into a blurred parchment-tinted bar
 *    after the user scrolls 50px (state `scrolled`).
 *  - On `<lg` (1024px) the inline nav collapses into a hamburger that opens
 *    a fullscreen overlay (state `open`). Body scroll is locked while open.
 *  - Logo is the restaurant emblem (circular). Brand label shrinks to
 *    "Tenería" on small screens to keep the bar single-line, expands to
 *    "Restaurante Museo Tenería" on md+ and adds "· MCDLXX" on 2xl+.
 *  - Each desktop link shows the Roman numeral on hover, matching the
 *    "Capítulo" pattern used inside content pages.
 *
 * Mobile UX guarantees (≤lg):
 *  - The "Reservar" CTA is hidden because it is already visible inside the
 *    hamburger overlay (avoids 4-element navbar overlap on small phones).
 *  - The phone call shortcut and hamburger keep generous touch targets.
 *  - The overlay snaps to a solid parchment-dark background **immediately**
 *    (no fade-through of the page underneath); only its inner content fades
 *    in, which fixes the "transparent menu" perception on slow phones.
 *
 * Accessibility:
 *  - <header role implied>; hamburger toggles aria-label and locks scroll.
 *  - All links have data-testid for end-to-end testing.
 */
import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, Phone } from "lucide-react";
import LanguageSwitcher from "@/components/layout/LanguageSwitcher";

/**
 * Order of the public sections + the Roman numeral shown on hover.
 * Adding/removing items here is the single source of truth for nav order.
 * `labelKey` is the i18n path resolved at render time by `t()`.
 */
const NAV = [
    { to: "/gastronomia", labelKey: "nav.gastronomia", roman: "I", testid: "nav-link-gastronomia" },
    { to: "/museo", labelKey: "nav.museo", roman: "II", testid: "nav-link-museo" },
    { to: "/eventos", labelKey: "nav.eventos", roman: "III", testid: "nav-link-eventos" },
    { to: "/premios", labelKey: "nav.premios", roman: "IV", testid: "nav-link-premios" },
    { to: "/blog", labelKey: "nav.blog", roman: "V", testid: "nav-link-blog" },
    { to: "/reservas", labelKey: "nav.reservas", roman: "VI", testid: "nav-link-reservas" },
    { to: "/contacto", labelKey: "nav.contacto", roman: "VII", testid: "nav-link-contacto" },
];

export default function Navbar() {
    const { t } = useTranslation();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Lock body scroll while the fullscreen menu is open.
    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
    }, [open]);

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-40 transition-all duration-700 ease-out ${
                scrolled
                    ? "bg-bg/85 backdrop-blur-xl border-b border-gold/15 py-3"
                    : "bg-transparent py-4 md:py-6"
            }`}
        >
            <div className="max-w-[1500px] mx-auto px-4 md:px-12 flex items-center justify-between gap-3">
                {/* Brand ---------------------------------------------------- */}
                <Link
                    to="/"
                    data-testid="nav-logo"
                    data-cursor="hover"
                    className="flex items-center gap-2 md:gap-3 group min-w-0"
                >
                    <img
                        src="/logo-teneria.jpg"
                        alt="Restaurante Museo Tenería"
                        className="w-9 h-9 md:w-11 md:h-11 rounded-full object-cover bg-ivory border border-gold/30 shadow-sm shrink-0"
                    />
                    <span className="flex items-baseline gap-2 min-w-0">
                        <span className="font-display italic text-base sm:text-lg md:text-xl xl:text-2xl text-gold leading-none tracking-wide whitespace-nowrap truncate">
                            <span className="hidden md:inline">Restaurante Museo </span>Tenería
                        </span>
                        <span className="hidden 2xl:inline-block font-editorial italic text-[0.65rem] text-stone tracking-[0.35em] uppercase relative top-[-2px]">
                            · MCDLXX
                        </span>
                    </span>
                </Link>

                {/* Desktop nav (≥lg) --------------------------------------- */}
                <nav className="hidden lg:flex items-center gap-6 xl:gap-9">
                    {NAV.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            data-testid={item.testid}
                            data-cursor="hover"
                            className={({ isActive }) =>
                                `relative flex items-baseline gap-2 group transition-colors ${
                                    isActive ? "text-gold" : "text-ivory hover:text-gold"
                                }`
                            }
                        >
                            <span className="font-editorial italic text-[0.65rem] text-gold/70 opacity-0 group-hover:opacity-100 transition-opacity duration-500 absolute -top-3 left-0">
                                {item.roman}
                            </span>
                            <span className="font-body text-sm tracking-wider">
                                {t(item.labelKey)}
                            </span>
                        </NavLink>
                    ))}
                </nav>

                {/* Right-side actions -------------------------------------- */}
                <div className="flex items-center gap-2 md:gap-4 shrink-0">
                    {/* Language switcher — desktop inline, hidden on mobile (lives in hamburger) */}
                    <div className="hidden lg:block">
                        <LanguageSwitcher variant="inline" />
                    </div>
                    {/* Phone — only visible <lg as a quick-call shortcut */}
                    <a
                        href="tel:+34925484633"
                        aria-label={t("nav.call")}
                        className="lg:hidden w-10 h-10 flex items-center justify-center text-gold border border-gold/40 hover:bg-gold hover:text-bg transition-colors"
                        data-testid="nav-call-button-mobile"
                    >
                        <Phone size={15} />
                    </a>
                    {/* Reservar — hidden on mobile/tablet (already inside the hamburger).
                        Wrapped in a span because `.btn-ghost` sets `display: inline-flex`
                        which overrides Tailwind's `hidden` utility. */}
                    <span className="hidden lg:inline-flex">
                        <Link
                            to="/reservas"
                            className="btn-ghost"
                            data-testid="nav-reservar-button"
                            data-cursor="hover"
                        >
                            {t("nav.reservar")}
                        </Link>
                    </span>
                    {/* Hamburger — only <lg */}
                    <button
                        type="button"
                        aria-label={t("nav.openMenu")}
                        onClick={() => setOpen(true)}
                        data-testid="nav-hamburger-open"
                        className="lg:hidden w-10 h-10 flex items-center justify-center text-ivory border border-gold/40 hover:text-gold transition-colors"
                    >
                        <Menu size={20} />
                    </button>
                </div>
            </div>

            {/* Fullscreen overlay menu ------------------------------------- */}
            {/*
              Two-layer pattern so the background snaps to solid immediately
              (no fade-through of the underlying page) while inner content
              still has a graceful fade/slide-in animation:
                - `<div>` wrapper toggles only `pointer-events` and uses no
                  opacity transition. It is either fully rendered or hidden.
                - The inner `<motion>`-free div animates content opacity/transform.
            */}
            <div
                className={`fixed inset-0 z-50 ${
                    open ? "pointer-events-auto" : "pointer-events-none invisible"
                }`}
                style={{ background: "var(--bg)" }}
                aria-hidden={!open}
            >
                {/* Solid base — opaque from frame 1 */}
                <div className="absolute inset-0" style={{ background: "var(--bg)" }} />
                <div className="absolute inset-0 paper-grain" />

                {/* Header bar inside the overlay */}
                <div className="relative flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gold/15">
                    <div className="flex items-center gap-3 min-w-0">
                        <img
                            src="/logo-teneria.jpg"
                            alt="Restaurante Museo Tenería"
                            className="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover bg-ivory border border-gold/30 shrink-0"
                        />
                        <span className="font-display italic text-base sm:text-xl md:text-2xl text-gold truncate">
                            <span className="hidden sm:inline">Restaurante Museo </span>Tenería
                        </span>
                    </div>
                    <button
                        onClick={() => setOpen(false)}
                        aria-label={t("nav.closeMenu")}
                        data-testid="nav-hamburger-close"
                        className="w-10 h-10 flex items-center justify-center text-ivory hover:text-gold transition-colors shrink-0"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Animated menu items — opacity/translate run only on open */}
                <nav
                    className={`relative flex flex-col items-center justify-center gap-5 sm:gap-7 pt-10 sm:pt-16 px-6 transition-all duration-500 ease-out ${
                        open ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                    }`}
                >
                    {NAV.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            onClick={() => setOpen(false)}
                            data-testid={`mobile-${item.testid}`}
                            className={({ isActive }) =>
                                `flex items-baseline gap-3 ${
                                    isActive ? "text-gold" : "text-ivory"
                                }`
                            }
                        >
                            <span className="font-editorial italic text-xs sm:text-sm text-gold/60">
                                {item.roman}
                            </span>
                            <span className="font-display italic text-4xl sm:text-5xl tracking-tight">
                                {t(item.labelKey)}
                            </span>
                        </NavLink>
                    ))}
                    <Link
                        to="/reservas"
                        onClick={() => setOpen(false)}
                        className="btn-solid-alt mt-6 sm:mt-8"
                        data-testid="mobile-nav-reservar"
                    >
                        {t("nav.reservarMesa")}
                    </Link>
                    {/* Language switcher inside the mobile menu */}
                    <div className="mt-8 pt-6 border-t border-gold/15 w-full max-w-xs flex justify-center">
                        <LanguageSwitcher variant="stack" />
                    </div>
                </nav>
            </div>
        </header>
    );
}
